import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "@/components/feature/Pagination";
import { mvList } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";
import { formatViews } from "@/utils/format";

const PAGE_SIZE = 8;
const tabs = [
  { key: "latest", label: "最新", desc: "按发布时间排序，追踪刚刚上线的 MV" },
  { key: "hot", label: "最热", desc: "综合播放与热度值，当下最火的 MV" },
  { key: "trending", label: "流行", desc: "热度持续攀升，正在被大量安利的作品" },
];

export default function ChartsPage() {
  const [tab, setTab] = useState("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title: "红桃MV视频 - MV排行榜 - 最新最热音乐视频榜单",
    description:
      "红桃MV视频排行榜，实时更新最新、最热、流行的 MV 榜单，帮你快速发现当下最受欢迎的音乐视频作品。",
    keywords: "红桃MV视频,MV排行榜,最新MV,热门MV,音乐视频榜单",
  });

  const sorted = useMemo(() => {
    const list = [...mvList];
    if (tab === "latest") return list.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    if (tab === "hot") return list.sort((a, b) => b.views - a.views);
    return list.sort((a, b) => b.trending - a.trending);
  }, [tab]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const items = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const activeTab = tabs.find((t) => t.key === tab);

  return (
    <div className="w-full">
      <div className="border-b border-background-800 bg-background-900/50 px-4 py-10 md:px-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500/15 text-2xl text-primary-400">
            <i className="ri-bar-chart-2-line" aria-hidden="true"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-4xl">MV 排行榜</h1>
            <p className="mt-1 text-sm text-foreground-400">基于播放量与热度实时更新，发现当下最受关注的 MV</p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={`cursor-pointer rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                tab === t.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-800 text-foreground-300 hover:bg-background-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {activeTab && <p className="mt-3 text-xs text-foreground-500">{activeTab.desc}</p>}
      </div>

      <div className="w-full px-4 py-8 md:px-8">
        <div className="overflow-hidden rounded-lg border border-background-800">
          {items.map((mv, i) => {
            const rank = (current - 1) * PAGE_SIZE + i + 1;
            return (
              <Link
                key={mv.id}
                to={`/detail/${mv.id}`}
                className={`group flex items-center gap-4 px-3 py-3 transition-colors hover:bg-background-800 md:px-5 ${
                  i % 2 === 0 ? "bg-background-900" : "bg-background-900/60"
                }`}
              >
                <span
                  className={`w-8 shrink-0 text-center font-heading text-xl font-bold ${
                    rank <= 3 ? "text-accent-500" : "text-foreground-500"
                  }`}
                >
                  {rank}
                </span>
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md md:h-16 md:w-16">
                  <img src={mv.cover} alt={`${mv.title} 缩略图`} title={`${mv.title} - ${mv.artist}`} className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-400">{mv.title}</h3>
                  <p className="mt-1 truncate text-xs text-foreground-400">
                    {mv.artist} · {mv.region}
                  </p>
                  <p className="mt-1 hidden text-xs text-foreground-500 sm:block">{mv.releaseDate} · {mv.duration}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="flex items-center justify-end gap-1 text-sm text-foreground-200">
                    <i className="ri-play-circle-line text-primary-400" aria-hidden="true"></i>
                    {formatViews(mv.views)}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">热度 {mv.trending}</p>
                </div>
              </Link>
            );
          })}
        </div>
        <Pagination page={current} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}